import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { useMutation } from "@tanstack/react-query";
import { apiUsersmeFormatGet } from "@/generated/api";
import { logoutMutation } from "@/generated/api/@tanstack/react-query.gen";

type TAuthUser = {
  accountId: string;
  email?: string;
  username?: string;
};

export type TAuthContext = {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  check: () => Promise<boolean>;
  user: TAuthUser | null;
};

const UserContext = createContext<TAuthContext>({
  login: async () => {},
  logout: async () => {},
  check: async () => false,
  user: null,
});

export default function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<TAuthUser | null>(null);
  const logoutApiMutation = useMutation({
    ...logoutMutation(),
  });

  const login = useCallback(async () => {}, []);

  const logout = useCallback(async () => {
    try {
      await logoutApiMutation.mutateAsync({});
    } finally {
      setUser(null);
      window.location.href = "/login";
    }
  }, []);

  const check = useCallback(async () => {
    if (user) return true;

    const res = await apiUsersmeFormatGet({ throwOnError: false });
    if (res.data?.accountId) {
      setUser({
        accountId: res.data.accountId,
        email: res.data.email,
        username: res.data.username,
      });
      return true;
    }

    return false;
  }, [user]);

  return (
    <UserContext.Provider value={{ user: user, login, logout, check }}>
      {children}
    </UserContext.Provider>
  );
}

export const useAuthProvider = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("Page must in app folder");
  }

  return context;
};
